import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, ExternalLink, MailWarning, Pencil, UserX, Users } from 'lucide-react';
import { deactivateUser, listUsers, setUserRole } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { AdminUser } from '@/lib/api/types';
import { downloadCsv } from '@/lib/csv';
import { formatDate, formatDateTime, initials, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { Badge, IdChip, type Tone } from '@/components/ui/badge';
import { Button, IconButton } from '@/components/ui/button';
import { Prop } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Dialog, Drawer } from '@/components/ui/dialog';
import { Notice } from '@/components/ui/feedback';
import { FilterSelect, Select } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { TableToolbar } from '@/components/ui/toolbar';
import { toast } from '@/components/ui/toast';
import { useOrganizations } from '@/features/organizations/use-organizations';
import { ROLE_LABEL, ROLES } from './roles';

/**
 * Every account on the platform. The role shown and edited is the one on
 * the account's FIRST membership — that is what `/admin/users` reports and
 * what `PATCH /admin/users/:id/role` changes.
 */
export function UsersPage() {
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: listUsers });
  const organizations = useOrganizations();

  const [q, setQ] = useUrlParam('q');
  const [role, setRole] = useUrlParam('role', 'all');
  const [verified, setVerified] = useUrlParam('verified', 'all');
  const [org, setOrg] = useUrlParam('org', 'all');
  const [openId, setOpenId] = useUrlParam('id');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const filtersActive = !!q || role !== 'all' || verified !== 'all' || org !== 'all';
  const reset = () => { setQ(''); setRole('all'); setVerified('all'); setOrg('all'); };

  const filtered = useMemo(() => {
    let rows = users.data ?? [];
    if (query) rows = rows.filter((u) => `${u.displayName} ${u.email} ${organizations.label(u.organizationId)}`.toLowerCase().includes(query) || u.id.startsWith(query) || (u.organizationId ?? '').startsWith(query));
    if (role === 'none') rows = rows.filter((u) => !u.role);
    else if (role !== 'all') rows = rows.filter((u) => u.role === role);
    if (verified !== 'all') rows = rows.filter((u) => u.emailVerified === (verified === 'yes'));
    if (org !== 'all') rows = rows.filter((u) => u.organizationId === org);
    return rows;
  }, [users.data, query, role, verified, org, organizations]);

  const open = openId ? (users.data ?? []).find((u) => u.id === openId) ?? null : null;

  const exportCsv = () => downloadCsv(`users-${new Date().toISOString().slice(0, 10)}.csv`,
    ['id', 'Имя', 'E-mail', 'E-mail подтверждён', 'Роль', 'Организация', 'organizationId', 'hotelId', 'Создан'],
    filtered.map((u) => [u.id, u.displayName, u.email, u.emailVerified ? 'да' : 'нет', u.role ? ROLE_LABEL[u.role] ?? u.role : '', u.organizationId ? organizations.label(u.organizationId) : '', u.organizationId, u.hotelId, u.createdAt]));

  const roleTone = (r: string): Tone => (r === 'owner' ? 'accent' : r === 'super_admin' ? 'warn' : 'neutral');

  const columns: Column<AdminUser>[] = [
    { key: 'name', header: 'Пользователь', sortValue: (u) => u.displayName || u.email, render: (u) => (
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent-ink">{initials(u.displayName || u.email)}</span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-semibold text-ink">
            <span className="truncate">{u.displayName || u.email}</span>
            {u.emailVerified ? <CheckCircle2 size={13} className="shrink-0 text-ok" aria-label="E-mail подтверждён" /> : <MailWarning size={13} className="shrink-0 text-warn" aria-label="E-mail не подтверждён" />}
          </div>
          <div className="truncate text-xs text-muted">{u.email}</div>
        </div>
      </div>
    ) },
    { key: 'role', header: 'Роль', sortValue: (u) => (u.role ? ROLE_LABEL[u.role] ?? u.role : null), render: (u) => u.role ? <Badge tone={roleTone(u.role)}>{ROLE_LABEL[u.role] ?? u.role}</Badge> : <span className="text-xs text-faint">без членства</span> },
    { key: 'org', header: 'Организация', sortValue: (u) => (u.organizationId ? organizations.label(u.organizationId) : null), stopClick: true, hideBelow: 'md', render: (u) => u.organizationId ? (
      <>
        <Link to={`/organizations/${u.organizationId}`} className="block max-w-[200px] truncate text-xs font-medium text-ink hover:text-accent-ink hover:underline">{organizations.label(u.organizationId)}</Link>
        <div className="mt-0.5"><IdChip id={u.organizationId} /></div>
      </>
    ) : <span className="text-xs text-faint">—</span> },
    { key: 'createdAt', header: 'Создан', sortValue: (u) => u.createdAt, hideBelow: 'lg', className: 'whitespace-nowrap text-xs text-muted', render: (u) => formatDate(u.createdAt) },
    { key: 'actions', header: 'Действия', align: 'right', width: 'w-28', stopClick: true, render: (u) => (
      <div className="flex justify-end gap-1">
        <IconButton size="sm" title="Изменить роль" onClick={() => setEditing(u)} disabled={!u.role || u.role === 'super_admin'}><Pencil size={14} /></IconButton>
        <IconButton size="sm" title="Деактивировать" onClick={() => setRemoving(u)} className="hover:bg-danger-soft hover:text-danger"><UserX size={14} /></IconButton>
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader title="Пользователи" subtitle={users.data ? `${plural(users.data.length, ['аккаунт', 'аккаунта', 'аккаунтов'])} во всех организациях` : undefined} />

      <TableToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Имя, e-mail, организация или id' }}
        shown={filtered.length}
        total={users.data?.length}
        onReset={filtersActive ? reset : null}
        actions={<Button variant="outline" size="sm" icon={<Download size={14} />} onClick={exportCsv} disabled={filtered.length === 0}>CSV</Button>}
      >
        <FilterSelect ariaLabel="Роль" value={role} onChange={setRole} options={[{ value: 'all', label: 'Все роли' }, ...ROLES, { value: 'super_admin', label: ROLE_LABEL.super_admin }, { value: 'none', label: 'Без членства' }]} />
        <FilterSelect ariaLabel="E-mail" value={verified} onChange={setVerified} options={[{ value: 'all', label: 'Любой e-mail' }, { value: 'yes', label: 'Подтверждён' }, { value: 'no', label: 'Не подтверждён' }]} />
        <FilterSelect ariaLabel="Организация" value={org} onChange={setOrg} options={[{ value: 'all', label: 'Все организации' }, ...organizations.orgs.map((o) => ({ value: o.id, label: organizations.label(o.id) }))]} />
      </TableToolbar>

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(u) => u.id}
        defaultSort={{ key: 'createdAt', dir: 'desc' }}
        loading={users.isPending}
        error={users.error}
        onRetry={() => users.refetch()}
        empty={{ icon: <Users size={28} />, title: users.data?.length ? 'Ничего не подошло под фильтры' : 'Пользователей нет', action: filtersActive ? <Button size="sm" variant="ghost" onClick={reset}>Сбросить фильтры</Button> : undefined }}
        onRowClick={(u) => setOpenId(u.id)}
      />

      {open && (
        <Drawer
          open
          onClose={() => setOpenId('')}
          title={<span className="flex items-center gap-2">{open.displayName || open.email}{open.role && <Badge tone={roleTone(open.role)}>{ROLE_LABEL[open.role] ?? open.role}</Badge>}</span>}
          subtitle={open.email}
          footer={(
            <>
              {open.organizationId && <Link to={`/organizations/${open.organizationId}`} className="mr-auto inline-flex h-8 items-center gap-1.5 text-xs font-semibold text-accent-ink hover:underline"><ExternalLink size={13} />Организация</Link>}
              <Button size="sm" variant="outline" icon={<Pencil size={13} />} disabled={!open.role || open.role === 'super_admin'} onClick={() => setEditing(open)}>Роль</Button>
              <Button size="sm" variant="danger" icon={<UserX size={13} />} onClick={() => setRemoving(open)}>Деактивировать</Button>
            </>
          )}
        >
          {!open.emailVerified && <Notice tone="warn" icon={<MailWarning size={16} />} title="E-mail не подтверждён" className="mb-4">Аккаунт зарегистрирован, но ссылку из письма ещё не открывали.</Notice>}
          <dl className="divide-y divide-line">
            <Prop label="ID" mono><IdChip id={open.id} /></Prop>
            <Prop label="Организация">{open.organizationId ? <div className="text-right"><div>{organizations.label(open.organizationId)}</div><IdChip id={open.organizationId} /></div> : '—'}</Prop>
            <Prop label="Отель по умолчанию">{open.hotelId ? <Link to={`/hotels/${open.hotelId}`} className="text-accent-ink hover:underline"><IdChip id={open.hotelId} /></Link> : 'вся организация'}</Prop>
            <Prop label="Зарегистрирован">{formatDateTime(open.createdAt)}</Prop>
          </dl>
        </Drawer>
      )}

      {editing && <RoleDialog user={editing} onClose={() => setEditing(null)} />}
      {removing && <DeactivateDialog user={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

function RoleDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState(user.role && ROLES.some((r) => r.value === user.role) ? user.role : 'receptionist');
  const save = useMutation({
    mutationFn: () => setUserRole(user.id, role),
    onSuccess: () => { toast('success', 'Роль обновлена', `${user.displayName || user.email} → ${ROLE_LABEL[role]}`); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); onClose(); },
    onError: (err) => toast('error', 'Не удалось сохранить', describeError(err)),
  });
  return (
    <Dialog open onClose={onClose} title="Изменить роль" description="Роль на первом членстве аккаунта — так её отдаёт и меняет бэкенд."
      footer={<><Button variant="outline" onClick={onClose}>Отмена</Button><Button loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button></>}>
      <div className="space-y-4">
        <div className="rounded-lg bg-surface-2 p-3"><p className="text-sm font-semibold text-ink">{user.displayName || user.email}</p><p className="text-xs text-muted">{user.email}</p></div>
        <Select label="Роль" value={role} onChange={(e) => setRole(e.target.value)} options={ROLES} />
      </div>
    </Dialog>
  );
}

function DeactivateDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () => deactivateUser(user.id),
    onSuccess: () => { toast('info', 'Пользователь деактивирован', `${user.email} больше не сможет войти.`); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); qc.invalidateQueries({ queryKey: ['admin', 'stats'] }); onClose(); },
    onError: (err) => toast('error', 'Не удалось деактивировать', describeError(err)),
  });
  return (
    <Dialog open onClose={onClose} title="Деактивировать пользователя?"
      footer={<><Button variant="outline" onClick={onClose}>Отмена</Button><Button variant="danger" loading={run.isPending} onClick={() => run.mutate()}>Деактивировать</Button></>}>
      <div className="space-y-3">
        <p className="text-sm text-ink"><span className="font-semibold">{user.displayName || user.email}</span> ({user.email}) потеряет доступ ко всем своим организациям.</p>
        <Notice tone="neutral" title="Это не удаление">Бэкенд ставит аккаунту isActive=false и ничего не стирает — иначе организации, которыми он владеет, остались бы без владельца. В списке такой аккаунт пока не помечается: API не отдаёт isActive.</Notice>
      </div>
    </Dialog>
  );
}
